import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, gbp } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import Seo from "@/components/Seo";

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const { clearCart } = useStore();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);
  const sessionId = params.get("session_id");
  const orderId = params.get("order");

  useEffect(() => {
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          api.get(`/orders/${data.order_id || orderId}`).then((r) => setOrder(r.data)).catch(() => {});
          return;
        }
        if (data.status === "expired" || attempts >= 6) { setState("failed"); return; }
        attempts += 1; setTimeout(poll, 2000);
      } catch { setState("failed"); }
    };
    if (sessionId) poll(); else setState("failed");
  }, [sessionId, orderId, clearCart]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center min-h-[60vh]">
      <Seo title="Order Confirmation" />
      {state === "checking" && (<><Loader2 className="w-12 h-12 mx-auto text-brand-pink animate-spin mb-4" /><h1 className="font-serif-display text-3xl">Confirming your payment…</h1><p className="text-muted-foreground mt-2">Just a moment while we check things over.</p></>)}
      {state === "paid" && (
        <div className="ck-fade-up">
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#2D6A4F] mb-4" />
          <h1 className="font-serif-display text-4xl mb-2" data-testid="order-success">Thank you for your order!</h1>
          {order && <p className="text-muted-foreground mb-1">Order <strong>{order.order_number}</strong></p>}
          <p className="text-muted-foreground mb-6">A confirmation email is on its way. I'll get started on your handmade pieces right away.</p>
          {order && (
            <div className="bg-brand-blush/40 rounded-sm p-6 text-left mb-6">
              <h3 className="font-serif-display text-xl mb-3">Summary</h3>
              {order.items.map((it, i) => (<div key={i} className="flex justify-between text-sm py-1"><span>{it.title} {it.colour ? `(${it.colour})` : ""} × {it.qty}</span><span>{gbp(it.unit_price * it.qty)}</span></div>))}
              <div className="flex justify-between text-sm border-t border-brand-border mt-2 pt-2"><span>Delivery</span><span>{order.delivery === 0 ? "Free" : gbp(order.delivery)}</span></div>
              {order.discount > 0 && <div className="flex justify-between text-sm text-brand-pink"><span>Discount</span><span>−{gbp(order.discount)}</span></div>}
              <div className="flex justify-between font-medium border-t border-brand-border mt-2 pt-2"><span>Total paid</span><span>{gbp(order.total)}</span></div>
            </div>
          )}
          <Link to="/shop" className="inline-block bg-brand-ink text-white px-8 py-3 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors">Continue shopping</Link>
        </div>
      )}
      {state === "failed" && (<><XCircle className="w-16 h-16 mx-auto text-destructive mb-4" /><h1 className="font-serif-display text-3xl mb-2">Payment not completed</h1><p className="text-muted-foreground mb-6">Your payment wasn't completed. Your basket is still saved if you'd like to try again.</p><Link to="/checkout" className="inline-block bg-brand-ink text-white px-8 py-3 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors">Return to checkout</Link></>)}
    </div>
  );
}
